import React, { useCallback, useContext, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import { useForm } from 'react-hook-form';
import Constants from 'expo-constants';
import ConstituentCard from './ConstituentCard';
import ContributionCard from './ContributionCard';
import PaymentMethod from './PaymentMethod';
import { Alert } from '../../common/Alert';
import Loading from '../../common/Loading';
import Recaptcha from '../../common/ReCaptcha';
import Utils from '../../../common/Utils';
import AppContext from '../../../contexts/App';
import GivingContext, { ActionTypes, PaymentMethods } from '../../../contexts/Giving';
import useAxios from '../../../hooks/useAxios';

let cartId;
let recaptchaToken;
let executingAddressVerification = false;

const calculateTotal = donations => {
    let total = 0;

    donations?.forEach(entry => {
        if (entry?.amount) {
            total += parseFloat(entry.amount);
        }
    });

    return total;
};

export const Form = ({ cmsKey, title, ...rest }) => {
    const { get, post } = useAxios();
    const {
        state: { settings }
    } = useContext(AppContext);
    const { state, dispatch } = useContext(GivingContext);
    const recaptchaRef = useRef();
    const total = calculateTotal(state.cartDetails.donations);

    const {
        clearErrors,
        control,
        formState: { errors, isSubmitting, isValid },
        getValues,
        handleSubmit,
        reset,
        resetField,
        setError,
        setValue,
        trigger
    } = useForm({
        mode: 'onBlur',
        shouldFocusError: true,
        shouldUnregister: true,
        defaultValues: {
            'donations.0.amount': null
        }
    });

    const validateAddress = useCallback(async () => {
        if (executingAddressVerification) {
            return;
        }

        const address = getValues('address');
        const address2 = getValues('address2');
        const city = getValues('city');
        const stateInput = getValues('state');
        const zip = getValues('zip');
        const country = getValues('country');

        let endpoint;
        let isUSAddress = true;

        if (zip?.length === 0) {
            return;
        }

        if (
            state.cartDetails.addressInfo?.verified &&
            state.cartDetails.addressInfo?.details?.line1?.equals(address) &&
            state.cartDetails.addressInfo?.details?.line2?.equals(address2) &&
            state.cartDetails.addressInfo?.details?.postal?.equals(zip)
        ) {
            return;
        }

        if (country?.toLowerCase().equals('us')) {
            if (address.length > 0 && zip?.length >= 5) {
                endpoint = `${settings?.smartyStreetsSettings?.usApiEndPoint}/street-address`;

                endpoint += `?auth-id=${settings?.smartyStreetsSettings?.clientSideKey}`;
                endpoint += '&match=enhanced&candidates=5&geocode=true';
                endpoint += `&street=${address}`;
                endpoint += `&street2=${address2?.replaceAll('#', '')}`;
                // endpoint += `&city=${city}`;
                // endpoint += `&state=${stateInput}`;
                endpoint += `&zipcode=${zip}`;
            }
        } else {
            if (address.length > 0 && zip?.length >= 6) {
                isUSAddress = false;

                endpoint = `${settings?.smartyStreetsSettings?.intlApiEndPoint}/verify`;

                endpoint += `?auth-id=${settings?.smartyStreetsSettings?.clientSideKey}`;
                endpoint += `&address1=${address}`;
                endpoint += `&address2=${address2}`;
                endpoint += `&postal_code=${zip}`;
                // endpoint += `&locality=${city}`;
                // endpoint += `&administrative_area=${stateInput}`;
                endpoint += `&country=${country}`;
            }
        }

        if (endpoint) {
            executingAddressVerification = true; // guard

            clearErrors(['address', 'city', 'state', 'zip']);

            const result = await get(endpoint);

            if (result?.length > 0) {
                if (isUSAddress) {
                    const matchCode = result[0].analysis.dpv_match_code;

                    setValue('address', result[0].delivery_line_1, { shouldTouch: true });
                    setValue('city', result[0].components?.city_name, { shouldTouch: true });
                    setValue('state', result[0].components?.state_abbreviation, { shouldTouch: true });

                    if (result[0].delivery_line_2 && matchCode === 'Y') {
                        setValue('address2', result[0].delivery_line_2, { shouldTouch: true });
                    } else if (matchCode === 'S') {
                        setValue('address2', '', { shouldTouch: true });
                    }

                    if (!result[0].components?.plus4_code) {
                        setValue('zip', result[0].components?.zipcode, { shouldTouch: true });
                    } else {
                        setValue('zip', `${result[0].components?.zipcode}-${result[0].components?.plus4_code}`, { shouldTouch: true });
                    }

                    if (matchCode === 'Y' || matchCode === 'D') {
                        dispatch({
                            type: ActionTypes.UPDATE_CART_DETAILS,
                            data: {
                                addressInfo: {
                                    accepted: true,
                                    verified: true,
                                    details: {
                                        line1: getValues('address'),
                                        line2: getValues('address2') || '',
                                        city: getValues('city'),
                                        state: getValues('state'),
                                        postalCode: getValues('zip')
                                    }
                                }
                            }
                        });
                    } else {
                        dispatch({
                            type: ActionTypes.UPDATE_CART_DETAILS,
                            data: {
                                addressInfo: {
                                    accepted: true,
                                    verified: false,
                                    details: {
                                        line1: getValues('address'),
                                        line2: getValues('address2') || '',
                                        city: getValues('city'),
                                        state: getValues('state'),
                                        postalCode: getValues('zip')
                                    }
                                }
                            }
                        });
                    }
                } else {
                    if (
                        result[0].analysis?.verification_status?.equals('Verified') ||
                        result[0].analysis?.verification_status?.equals('Partial') ||
                        result[0].analysis?.verification_status?.equals('Ambiguous')
                    ) {
                        setValue('address', result[0].address1, { shouldTouch: true });

                        if (result[0].address2 && !result[0].components.country_iso_3.equals('CAN')) {
                            setValue('address2', result[0].address2, { shouldTouch: true });
                        } else {
                            if (result[0].components?.sub_building && result[0].components.country_iso_3.equals('CAN')) {
                                setValue('address2', '', { shouldTouch: true });
                            }
                        }

                        if (result[0].components?.locality) {
                            setValue('city', result[0].components?.locality, { shouldTouch: true });
                        } else if (result[0].components?.dependent_locality) {
                            setValue('city', result[0].components?.dependent_locality, { shouldTouch: true });
                        }

                        setValue('state', result[0].components?.administrative_area, { shouldTouch: true });
                        setValue('zip', `${result[0].components?.postal_code}`, { shouldTouch: true });

                        dispatch({
                            type: ActionTypes.UPDATE_CART_DETAILS,
                            data: {
                                addressInfo: {
                                    accepted: true,
                                    verified: result[0].analysis?.verification_status?.equals('Verified'),
                                    details: {
                                        line1: getValues('address'),
                                        line2: getValues('address2') || '',
                                        city: getValues('city'),
                                        state: getValues('state'),
                                        postalCode: getValues('zip')
                                    }
                                }
                            }
                        });
                    }
                }
            } else {
                trigger('zip');

                dispatch({
                    type: ActionTypes.UPDATE_CART_DETAILS,
                    data: {
                        addressInfo: {
                            accepted: false,
                            verified: false,
                            details: {
                                line1: address || '',
                                line2: address2 || '',
                                city: city || '',
                                state: stateInput || '',
                                postalCode: zip
                            }
                        }
                    }
                });
            }

            executingAddressVerification = false;
            return true;
        }
    }, [
        settings?.smartyStreetsSettings?.clientSideKey,
        settings?.smartyStreetsSettings?.intlApiEndPoint,
        settings?.smartyStreetsSettings?.usApiEndPoint,
        clearErrors,
        dispatch,
        get,
        getValues,
        setValue,
        state,
        trigger
    ]);

    const validateEmailDomain = useCallback(async () => {
        const email = getValues('email');

        if (state.cartDetails.emailInfo?.verified && state.cartDetails.emailInfo?.value?.equals(email)) {
            return;
        }

        if (Utils.ValidEmailRegExp.test(email.toLowerCase())) {
            if (!state.cartDetails.emailInfo?.value?.equals(email)) {
                const result = await post(`${Constants.manifest?.extra?.apiUrl}/Util/Address/ValidateEmailDomain`, { email }, 'application/x-www-form-urlencoded');

                if (result) {
                    if (result.valid) {
                        clearErrors(['email']);

                        dispatch({
                            type: ActionTypes.UPDATE_CART_DETAILS,
                            data: {
                                emailInfo: {
                                    verified: true,
                                    value: email
                                }
                            }
                        });

                        return true;
                    } else {
                        dispatch({
                            type: ActionTypes.UPDATE_CART_DETAILS,
                            data: {
                                verified: false,
                                value: email
                            }
                        });

                        return result.message;
                    }
                } else {
                    dispatch({
                        type: ActionTypes.UPDATE_CART_DETAILS,
                        data: {
                            verified: false,
                            value: email
                        }
                    });

                    return 'Unable to verify email';
                }
            }
        }
    }, [clearErrors, dispatch, getValues, post, state.cartDetails.emailInfo]);

    const validatePhoneNumber = useCallback(async () => {
        const phoneNumber = getValues('mobile');
        const country = getValues('country');

        const cleanedPhoneNumber = phoneNumber.replaceAll('(', '').replaceAll(')', '').replaceAll('-', '').replaceAll(' ', '');

        if (cleanedPhoneNumber.length >= 10 && !state.cartDetails.phoneInfo?.value?.equals(cleanedPhoneNumber)) {
            const body = { phone: cleanedPhoneNumber, countryIso2: country };

            const result = await post(`${Constants.manifest?.extra?.apiUrl}/Util/Address/ValidatePhoneNumber`, body, 'application/x-www-form-urlencoded');

            if (result?.valid) {
                setValue('mobile', result.phoneNumber, { shouldTouch: true });

                clearErrors(['mobile']);

                dispatch({
                    type: ActionTypes.UPDATE_CART_DETAILS,
                    data: {
                        verified: true,
                        value: cleanedPhoneNumber
                    }
                });

                return true;
            }

            dispatch({
                type: ActionTypes.UPDATE_CART_DETAILS,
                data: {
                    verified: false,
                    value: cleanedPhoneNumber
                }
            });

            return 'Unable to verify phone number';
        }
    }, [clearErrors, dispatch, getValues, post, setValue, state.cartDetails.phoneInfo]);

    const postDonations = useCallback(async () => {
        const donationsToAdd = state.cartDetails.donations?.filter(donation => donation.amount && donation.amount > 0 && !donation.submitted);

        if (donationsToAdd.length > 0) {
            let successCount = 0;

            const makeRequest = async donation => {
                const body = {
                    cmsKey,
                    amount: parseFloat(donation.amount).toFixed(2),
                    f1FundCode: donation.fundCode,
                    f1FundTitle: donation.name,
                    recurringType: donation.recurringType
                };

                if (cartId) {
                    body.cartId = cartId;
                }

                const postDonationsResponse = await post(`${Constants.manifest?.extra?.apiUrl}/Cart/Cornerstone/Add/Donation`, body);

                if (postDonationsResponse) {
                    if (postDonationsResponse.success) {
                        donation.submitted = true;

                        if (!cartId) {
                            cartId = postDonationsResponse.cartId;
                        }

                        successCount++;
                    }
                }
            };

            for (const donation of donationsToAdd) {
                await makeRequest(donation);
            }

            return successCount === donationsToAdd.length;
        }

        return true;
    }, [cmsKey, post, state.cartDetails.donations]);

    const submit = async data => {
        recaptchaRef?.current?.execute();

        const donationsSucceeded = await postDonations(state.cartDetails.donations, false);

        if (!donationsSucceeded) {
            return Alert('Problem processing donation(s). Please review your data and try again.');
        }

        if (!cartId) {
            return Alert("Unable to complete you're request at this time. Please try again.");
        }

        if (state.cartDetails.addressInfo?.accepted) {
            const addCustomer = {
                shoppingCartType: 4, // TODO parameterize based on domain,
                cartId,
                firstName: data.firstName,
                lastName: data.lastName,
                emailAddress: data.email,
                phone: data.mobile === undefined ? null : data.mobile,
                addr1: data.address,
                city: data.city,
                state: data.state,
                zip: data.zip,
                country: data.country,
                addressQuality: state.cartDetails?.addressInfo?.verified ? 'verified' : 'manual'
            };

            if (data.address2) {
                addCustomer.addr2 = data.address2;
            }

            const addCustomerResponse = await post(`${Constants.manifest?.extra?.apiUrl}/Cart/AddCustomer`, addCustomer);

            if (!addCustomerResponse?.success) {
                return Alert('Problem associating contact with donation. Please try again');
            }

            if (!state.cartDetails.paymentInfo.token) {
                // not sure how this would happen but lets make sure we get a token
                const env = settings?.websiteSettings?.environment;
                const tokenizeEndpoint =
                    env?.equals('Development') || env?.equals('Staging')
                        ? settings?.cardConnectSettings?.test?.tokenizeEndpoint
                        : settings?.cardConnectSettings?.us?.tokenizeEndpoint;

                const ccToken = await post(tokenizeEndpoint, { account: data.cardNumber }, undefined, 'application/json');

                if (ccToken?.errorcode !== 0 || !ccToken?.token) {
                    return Alert('Credit Card could not be processed, please try again.');
                }
            }

            if (!errors || Object.keys(errors).length === 0) {
                try {
                    const body = {
                        shoppingCartType: 4, // Need to determine this based on domain or wrapper
                        cartId: cartId,
                        ccToken: state.cartDetails.paymentInfo?.token,
                        recaptchaResponse: recaptchaToken
                    };

                    if (state.cartDetails.paymentInfo?.method === PaymentMethods.CREDIT) {
                        body.cvv = data.verifyCode;
                        body.expMonth = data.monthYear.substring(0, 2);
                        body.expYear = data.monthYear.slice(-4);
                    }

                    const processAndCloseResponse = await post(`${Constants.manifest?.extra?.apiUrl}/Cart/ProcessAndClose`, body);

                    if (processAndCloseResponse?.success) {
                        reset();

                        setValue('donations.0.amount', ''); // Still trying to force this

                        cartId = undefined;
                        recaptchaToken = undefined;

                        dispatch({
                            type: ActionTypes.RESET_CART
                        });
                    }

                    return Alert(processAndCloseResponse?.message);
                } catch (err) {
                    return Alert('Problem processing donation(s). Please try again');
                }
            }
        } else {
            return Alert('You entries could not be validated. Please verify your entries and try again.');
        }

        recaptchaToken = undefined;
        recaptchaRef.current?.reset();
    };

    return (
        <View style={Styles.wrapper}>
            <View>
                <ContributionCard
                    clearErrors={clearErrors}
                    control={control}
                    errors={errors}
                    getValues={getValues}
                    setError={setError}
                    setValue={setValue}
                    total={total}
                    title={title}
                    {...rest}
                />
            </View>

            <PaymentMethod clearErrors={clearErrors} control={control} errors={errors} getValues={getValues} resetField={resetField} setValue={setValue} {...rest} />

            {/* <ConstituentCard
                clearErrors={clearErrors}
                control={control}
                errors={errors}
                getValues={getValues}
                handleSubmit={handleSubmit}
                isValid={isValid}
                setValue={setValue}
                submit={submit}
                total={total}
                validateAddress={validateAddress}
                validateEmailDomain={validateEmailDomain}
                validatePhoneNumber={validatePhoneNumber}
                {...rest}
            /> */}
            {isSubmitting && <Loading />}
            <Recaptcha ref={recaptchaRef} onChange={token => (recaptchaToken = token)} />
        </View>
    );
};

const Styles = StyleSheet.create({
    wrapper: {
        backgroundColor: 'transparent',
        width: '90%'
    }
});
